import { useEffect, useState } from "react";
import { InlineWidget } from "react-calendly";

import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import { slackHook } from "src/utils/snug/slackHooks";
import { SignupStepper } from "src/sections/starter-pay/signup-stepper";
import {
  clearStarterPayContext,
  readStarterPayContext,
  type StarterPayContext,
} from "src/sections/starter-pay/context";
import "./starter-pay.css";

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

const CALENDLY_URL =
  "https://calendly.com/d/ckcb-3q9-qnx/snug-orientation?hide_gdpr_banner=1&utm_source=starter-pay";

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M5.5 3l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function StarterPayOnboardingPage() {
  const router = useRouter();
  const [context, setContext] = useState<StarterPayContext | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);

  useEffect(() => {
    const ctx = readStarterPayContext();
    if (ctx) {
      setContext(ctx);
      setHydrated(true);
      return;
    }
    // Preview escape-hatch: `?preview=1` lets us view the page without coming
    // from a real Account step.
    if (new URLSearchParams(window.location.search).get("preview") === "1") {
      setContext({
        name: "Preview User",
        company: "Preview Firm",
        email: "preview@example.com",
        plan: "annual",
        planLabel: "Starter",
        paymentSchedule: "single",
      });
      setHydrated(true);
      return;
    }
    router.replace("/get-started-account");
  }, [router]);

  if (!isGetSnugDomain) return null;
  if (!hydrated || !context) return null;

  const handleWithRep = () => {
    setShowCalendly(true);
    slackHook(
      `📞 STARTER PAY — Onboarding choice: WITH REP\n` +
        `Name: ${context.name}\n` +
        `Email: ${context.email}\n` +
        `Company: ${context.company}`,
    );
  };

  const handleSelfServe = () => {
    slackHook(
      `🚀 STARTER PAY — Onboarding choice: SELF\n` +
        `Name: ${context.name}\n` +
        `Email: ${context.email}\n` +
        `Company: ${context.company}`,
    );
    clearStarterPayContext();
    router.push(paths.dashboard.upgrade);
  };

  const handleSkip = () => {
    slackHook(
      `⏭ STARTER PAY — Onboarding choice: SKIP\n` +
        `Name: ${context.name}\n` +
        `Email: ${context.email}\n` +
        `Company: ${context.company}`,
    );
    clearStarterPayContext();
    router.push(paths.dashboard.index);
  };

  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-inner">
        <h1 className="sp-headline">
          Get started with <em>Snug.</em>
        </h1>

        <p className="sp-sub">
          Includes full platform access, white-label setup, and onboarding
          support.
        </p>

        <SignupStepper currentStep="onboarding" />

        <div className="sp-card">
          <div className="sp-card-head">
            <h2 className="sp-section-title">Pick your onboarding path</h2>
            <p className="sp-section-help">
              Choose how you'd like to get up and running. You can change your
              mind any time from your dashboard.
            </p>
          </div>

          <div className="sp-option-stack">
            <button
              type="button"
              className={`sp-option-card${showCalendly ? " is-selected" : ""}`}
              onClick={handleWithRep}
            >
              <div className="sp-option-card-body">
                <span className="sp-option-card-title">
                  Onboard with a Snug Rep
                </span>
                <span className="sp-option-card-desc">
                  Book a 1-on-1 walkthrough. We'll help you go live, integrate
                  with your site, and get the most out of the platform.
                </span>
              </div>
              <span className="sp-option-card-arrow">
                <ArrowIcon />
              </span>
            </button>

            <button
              type="button"
              className="sp-option-card"
              onClick={handleSelfServe}
            >
              <div className="sp-option-card-body">
                <span className="sp-option-card-title">Set up on my own</span>
                <span className="sp-option-card-desc">
                  Walk through setup yourself — domain, branding, and
                  integration directly from your dashboard.
                </span>
              </div>
              <span className="sp-option-card-arrow">
                <ArrowIcon />
              </span>
            </button>
          </div>

          {showCalendly && (
            <div className="sp-calendly">
              <InlineWidget
                url={CALENDLY_URL}
                prefill={{
                  email: context.email,
                  name: context.name,
                }}
                styles={{ height: 720 }}
              />
            </div>
          )}
        </div>

        <button type="button" className="sp-skip-link" onClick={handleSkip}>
          Skip for now
        </button>
      </section>
    </div>
  );
}
